import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { arrow } from "@/designSystemIcons";
import { useBrowserCompatibility } from "@/hooks/useBrowserCompatability";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { MetaRatingIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { Subscription } from "@/redux/user/userSlice";
import { useState } from "react";
import { useRef } from "react";

import {
  navigationEvolutionsIcon,
  navigationHomeIcon,
  navigationMyClubIcon,
  navigationMyEvolutionsIcon,
  navigationPlayersIcon,
  navigationProfileIcon,
  navigationSquadBuilderIcon,
  navigationTacticsIcon,
  navigationUpgradeIcon,
} from "./NavIcons";
import { navigationMoreIcon } from "./NavIcons";
import { NavLinkTouchDevice } from "./NavLinkTouchDevice";

const Divider = () => <div className="h-0.25 w-full bg-panel-foreground" />;

const DrawerItem = ({
  label,
  to,
  currentRoute,
  icon,
  setIsDrawerOpen,
  externalLink = false,
}: {
  label: string;
  to: string;
  currentRoute: string;
  icon?: React.ReactNode;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  externalLink?: boolean;
}) => (
  <NavLinkTouchDevice
    to={to}
    currentRoute={currentRoute}
    onClick={() => setIsDrawerOpen(false)}
    isInDrawer
    externalLink={externalLink}
  >
    <div className="flex flex-row w-full justify-start gap-x-4">
      {icon ? (
        <div className="w-6 h-6">{icon}</div>
      ) : (
        <div className="w-6 h-6" />
      )}
      <span>{label}</span>
    </div>
  </NavLinkTouchDevice>
);

const DrawerHeader = ({
  onClose,
  handleMoreOpen,
  isMoreOpen,
}: {
  onClose: () => void;
  handleMoreOpen: () => void;
  isMoreOpen: boolean;
}) => {
  return (
    <div className="flex flex-row w-full justify-start items-center gap-x-4 pl-6 h-12 text-panel-icon">
      {isMoreOpen ? (
        <button
          className="flex justify-center items-center rotate-90 p-4 -m-3 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            handleMoreOpen();
          }}
        >
          <div className="w-4 h-4">{arrow}</div>
        </button>
      ) : (
        <img src={"/logo.png"} className="w-6 h-6" />
      )}
      {isMoreOpen ? <span>More</span> : <span>EasySBC</span>}
      <button className="ml-auto py-4 pl-4 pr-6 mr-2" onClick={onClose}>
        <CloseIcon color={"var(--color-panel-icon)"} />
      </button>
      <button
        className="absolute bottom-12 right-1 py-4 pl-4 pr-8"
        onClick={onClose}
      >
        <CloseIcon color={"var(--color-panel-icon)"} />
      </button>
    </div>
  );
};

export const Drawer = ({
  isDrawerOpen,
  currentRoute,
  setIsDrawerOpen,
  userIsLoggedIn,
  freeUser,
  paidUserButNotUltimate,
  canUseClubImport,
  userSubscription,
  handleClick,
}: {
  isDrawerOpen: boolean;
  currentRoute: string;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  userIsLoggedIn: boolean;
  freeUser: boolean;
  paidUserButNotUltimate: boolean;
  canUseClubImport: boolean;
  userSubscription: Subscription;
  needsExtraPadding: boolean;
  handleClick: ({ isPaywalled }: { isPaywalled: boolean }) => void;
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState<number | null>(null);
  const isMoreOpenRef = useRef<HTMLDivElement>(null);
  const drawerRef = useOutsideClick(() => {
    setIsDrawerOpen(false);
    handleMoreOpen(false);
  });
  const { isUnsupportedBrowser } = useBrowserCompatibility();

  // Capture the height when opening the more menu
  const handleMoreOpen = (open: boolean) => {
    if (isMoreOpenRef.current && open) {
      setDrawerHeight(isMoreOpenRef.current.offsetHeight);
    }
    setIsMoreOpen(open);
  };

  const DrawerContent = (
    <nav
      className={cn(
        "fixed flex justify-end w-full bg-black/60 right-0 top-0 bottom-0 z-100",
        isDrawerOpen ? "translate-x-0" : "translate-x-full",
      )}
    >
      <div
        className={cn(
          "w-[325px] h-full flex flex-col justify-end bg-panel-base border-l-2 border-l-panel-foreground transition-transform duration-200 pb-20",
          isDrawerOpen ? "translate-x-0" : "translate-x-full",
        )}
        ref={drawerRef}
      >
        <DrawerHeader
          isMoreOpen={isMoreOpen}
          onClose={() => setIsDrawerOpen(false)}
          handleMoreOpen={() => handleMoreOpen(false)}
        />
        {isMoreOpen ? (
          <div className="flex flex-col" style={{ height: drawerHeight }}>
            <DrawerItem
              label="News"
              to="/news"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              label="Careers"
              to="https://careers.easysbc.io/"
              externalLink
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              label="Who Are We"
              to="/who-are-we"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              label="Contact"
              to="/contact"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              label="Legal & FAQ"
              to="/legal"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />

            <Divider />
            <section className="flex flex-col mt-4 text-new-gray-450">
              <DrawerItem
                to="https://discord.gg/mmNtdnUcJf"
                currentRoute={currentRoute}
                setIsDrawerOpen={setIsDrawerOpen}
                externalLink
                label="Discord"
                icon={
                  <img
                    src={
                      import.meta.env.VITE_APP_ASSETS_BASE_URL +
                      "/fut23/misc/discord-v2.svg"
                    }
                    className="w-6 h-6"
                  />
                }
              />
              <DrawerItem
                to="https://twitter.com/easysbc"
                currentRoute={currentRoute}
                setIsDrawerOpen={setIsDrawerOpen}
                externalLink
                label="Twitter"
                icon={
                  <img
                    src={
                      import.meta.env.VITE_APP_ASSETS_BASE_URL +
                      "/fut23/misc/twitter.svg"
                    }
                    className="w-6 h-6"
                  />
                }
              />
              <DrawerItem
                to="https://www.instagram.com/easysbc/"
                currentRoute={currentRoute}
                setIsDrawerOpen={setIsDrawerOpen}
                externalLink
                label="Instagram"
                icon={
                  <img
                    src={
                      import.meta.env.VITE_APP_ASSETS_BASE_URL +
                      "/fut23/misc/instagram.svg"
                    }
                    className="w-7 h-7"
                  />
                }
              />
            </section>
          </div>
        ) : (
          <div ref={isMoreOpenRef}>
            <DrawerItem
              icon={navigationProfileIcon}
              label={userIsLoggedIn ? "Profile" : "Log in"}
              to={userIsLoggedIn ? "/profile" : "/login"}
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <Divider />
            <DrawerItem
              icon={navigationHomeIcon}
              label="Home"
              to="/"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              icon={navigationPlayersIcon}
              label="Players"
              to="/players"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              icon={navigationEvolutionsIcon}
              label="Evolutions"
              to="/evolutions"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              icon={navigationSquadBuilderIcon}
              label="Squad Builder"
              to="/squad-builder"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              icon={navigationTacticsIcon}
              label="Tactics"
              to="/tactics"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <Divider />
            {userSubscription !== "diamond" ? (
              <DrawerItem
                icon={navigationUpgradeIcon}
                label={
                  freeUser
                    ? "Upgrade"
                    : paidUserButNotUltimate
                      ? "Get Ultimate"
                      : ""
                }
                to="/subscription"
                currentRoute={currentRoute}
                setIsDrawerOpen={setIsDrawerOpen}
              />
            ) : (
              <DrawerItem
                icon={navigationUpgradeIcon}
                label="Subscription"
                to="/subscription"
                currentRoute={currentRoute}
                setIsDrawerOpen={setIsDrawerOpen}
              />
            )}
            <DrawerItem
              icon={navigationMyClubIcon}
              label="My Club"
              to="/my-club"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <DrawerItem
              icon={navigationMyEvolutionsIcon}
              label="My Evolutions"
              to="/evolutions/my-evolutions"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <Divider />
            <DrawerItem
              icon={<MetaRatingIcon />}
              label="Meta Rating"
              to="/meta-rating"
              currentRoute={currentRoute}
              setIsDrawerOpen={setIsDrawerOpen}
            />
            <button
              className="flex items-center gap-x-4 w-full h-12 px-6 text-new-gray-450"
              onClick={(e) => {
                e.stopPropagation();
                handleMoreOpen(!isMoreOpen);
              }}
            >
              {navigationMoreIcon}
              More
            </button>
          </div>
        )}
      </div>
    </nav>
  );

  return isUnsupportedBrowser
    ? isDrawerOpen
      ? DrawerContent
      : null
    : DrawerContent;
};
